/**
 * Інтервальні повторення: SM-2 з привязкою до дедлайну (див. D-006).
 *
 * Відмінності від класичного SM-2:
 *  1. Фаза заучування з кроками всередині сесії (як в Anki) — нова картка
 *     не «зникає» одразу на добу, а повертається через 1 і 10 хвилин.
 *  2. Стиснення інтервалів: жоден інтервал не виходить за дедлайн.
 *  3. Фінальний відрізок (останні 9 днів) — режим прогону: інтервали
 *     обмежені половиною залишку, щоб кожна картка спливла ще мінімум двічі.
 *
 * Модуль чистий: жодних звертань до Date.now() всередині — час завжди
 * приходить аргументом. Це робить його повністю детермінованим у тестах.
 */

import { DAY, MINUTE, daysBetween, dayStart } from './time.ts'

export type CardState = 'new' | 'learning' | 'review' | 'relearning'
export type Grade = 'again' | 'hard' | 'good' | 'easy'

export interface Card {
  id: string
  state: CardState
  /** Індекс кроку у фазі заучування / перезаучування. */
  step: number
  /** Поточний інтервал у днях (має сенс лише для `review`). */
  interval: number
  /** Легкість (ease factor) SM-2. */
  ease: number
  /** Момент наступного показу, epoch ms. */
  due: number
  reps: number
  lapses: number
  introduced: number | null
  lastReview: number | null
}

/** Кроки заучування нової картки, у мілісекундах. */
export const LEARNING_STEPS = [1 * MINUTE, 10 * MINUTE]
/** Кроки після забування. */
export const RELEARNING_STEPS = [10 * MINUTE]

export const INITIAL_EASE = 2.5
export const MIN_EASE = 1.3
export const MAX_EASE = 3.0

/** Інтервал після випуску з фази заучування, у днях. */
export const GRADUATING_INTERVAL = 1
/** Інтервал, якщо нову картку одразу оцінено як «легко». */
export const EASY_INTERVAL = 3
/** У скільки разів скорочується інтервал після забування. */
export const LAPSE_MULTIPLIER = 0.5
/** Скільки останніх днів перед дедлайном працює режим прогону. */
export const FINAL_STRETCH_DAYS = 9

export function newCard(id: string): Card {
  return {
    id,
    state: 'new',
    step: 0,
    interval: 0,
    ease: INITIAL_EASE,
    due: 0,
    reps: 0,
    lapses: 0,
    introduced: null,
    lastReview: null,
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v))
}

/**
 * Детермінований «розкид» інтервалу ±5% — щоб картки, введені одного дня,
 * не поверталися однією стіною. Замість випадковості — хеш від id,
 * тому результат відтворюваний у тестах.
 */
export function fuzz(intervalDays: number, id: string): number {
  if (intervalDays < 3) return intervalDays
  let h = 2166136261
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  const ratio = ((h >>> 0) % 1000) / 1000 // 0..0.999
  const spread = Math.max(1, Math.round(intervalDays * 0.05))
  const delta = Math.round((ratio - 0.5) * 2 * spread)
  return Math.max(1, intervalDays + delta)
}

/**
 * Обмежує інтервал так, щоб курс закрився до дедлайну.
 * `deadline` — момент початку дня дедлайну; якщо його немає, обмеження не діє.
 */
export function capInterval(intervalDays: number, now: number, deadline: number | null): number {
  if (!deadline) return Math.max(1, intervalDays)
  const left = daysBetween(now, deadline)
  if (left <= 0) return Math.max(1, intervalDays) // після поїздки — звичайний режим
  if (left <= FINAL_STRETCH_DAYS) {
    // Режим прогону: картка мусить спливти ще щонайменше двічі.
    return clamp(intervalDays, 1, Math.max(1, Math.floor(left / 2)))
  }
  return clamp(intervalDays, 1, left - 1)
}

export interface ReviewContext {
  now: number
  /** Початок дня дедлайну (epoch ms) або null, якщо дедлайну немає. */
  deadline: number | null
}

/** Наступний інтервал у днях для картки у стані `review`. */
function nextReviewInterval(card: Card, grade: Grade): number {
  const i = Math.max(1, card.interval)
  switch (grade) {
    case 'hard':
      return Math.max(i + 1, Math.round(i * 1.2))
    case 'good':
      return Math.max(i + 1, Math.round(i * card.ease))
    case 'easy':
      return Math.max(i + 2, Math.round(i * card.ease * 1.3))
    case 'again':
      return Math.max(1, Math.round(i * LAPSE_MULTIPLIER))
  }
}

function adjustEase(ease: number, grade: Grade): number {
  const delta = grade === 'again' ? -0.2 : grade === 'hard' ? -0.15 : grade === 'easy' ? 0.15 : 0
  return clamp(Number((ease + delta).toFixed(3)), MIN_EASE, MAX_EASE)
}

/** Планує показ через `days` діб від початку поточної доби (+ранок). */
function scheduleDays(days: number, now: number): number {
  return dayStart(now) + days * DAY
}

/**
 * Основна функція: приймає картку і оцінку, повертає **нову** картку.
 * Вхідна картка не мутується.
 */
export function review(card: Card, grade: Grade, ctx: ReviewContext): Card {
  const { now, deadline } = ctx
  const next: Card = {
    ...card,
    reps: card.reps + 1,
    lastReview: now,
    introduced: card.introduced ?? now,
  }

  if (card.state === 'new' || card.state === 'learning') {
    if (grade === 'again') {
      next.state = 'learning'
      next.step = 0
      next.due = now + LEARNING_STEPS[0]!
      return next
    }
    if (grade === 'easy') {
      next.state = 'review'
      next.step = 0
      next.interval = capInterval(EASY_INTERVAL, now, deadline)
      next.due = scheduleDays(next.interval, now)
      next.ease = adjustEase(next.ease, 'easy')
      return next
    }
    // hard — повторити поточний крок; good — наступний крок.
    const step = grade === 'hard' ? card.step : card.step + 1
    if (step >= LEARNING_STEPS.length) {
      next.state = 'review'
      next.step = 0
      next.interval = capInterval(GRADUATING_INTERVAL, now, deadline)
      next.due = scheduleDays(next.interval, now)
      return next
    }
    next.state = 'learning'
    next.step = step
    next.due = now + LEARNING_STEPS[step]!
    return next
  }

  if (card.state === 'relearning') {
    if (grade === 'again') {
      next.step = 0
      next.due = now + RELEARNING_STEPS[0]!
      return next
    }
    const step = grade === 'hard' ? card.step : card.step + 1
    if (step >= RELEARNING_STEPS.length) {
      next.state = 'review'
      next.step = 0
      next.interval = capInterval(Math.max(1, card.interval), now, deadline)
      next.due = scheduleDays(next.interval, now)
      return next
    }
    next.step = step
    next.due = now + RELEARNING_STEPS[step]!
    return next
  }

  // state === 'review'
  next.ease = adjustEase(card.ease, grade)
  if (grade === 'again') {
    next.state = 'relearning'
    next.step = 0
    next.lapses = card.lapses + 1
    next.interval = Math.max(1, Math.round(card.interval * LAPSE_MULTIPLIER))
    next.due = now + RELEARNING_STEPS[0]!
    return next
  }
  const raw = nextReviewInterval({ ...card, ease: next.ease }, grade)
  next.state = 'review'
  next.step = 0
  next.interval = capInterval(fuzz(raw, card.id), now, deadline)
  next.due = scheduleDays(next.interval, now)
  return next
}

/** Чи картку треба показати зараз. */
export function isDue(card: Card, now: number): boolean {
  return card.state !== 'new' && card.due <= now
}

/**
 * Порядок черги повторень: спершу прострочені найдовше, потім за станом
 * (перезаучування важливіше за звичайне повторення).
 */
export function compareQueue(a: Card, b: Card): number {
  const weight = (c: Card) => (c.state === 'relearning' ? 0 : c.state === 'learning' ? 1 : 2)
  const w = weight(a) - weight(b)
  if (w !== 0) return w
  return a.due - b.due
}

/** Зведення для екрана прогресу. */
export interface Retention {
  known: number
  learning: number
  fresh: number
}

export function summarize(cards: Card[], now: number): Retention {
  let known = 0
  let learning = 0
  let fresh = 0
  for (const c of cards) {
    if (c.state === 'new') fresh++
    else if (c.state === 'review' && c.interval >= 7 && c.due > now) known++
    else learning++
  }
  return { known, learning, fresh }
}
